import { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { SearchSort } from '@pms/shared';

export interface SearchUrlState {
  destination: string;
  checkIn: string;
  checkOut: string;
  guests: number;
  priceMin?: number | undefined;
  priceMax?: number | undefined;
  amenities: string[];
  sort: SearchSort;
}

const SORTS: SearchSort[] = ['recommended', 'price_asc', 'price_desc'];

/**
 * The URL query string is the single source of truth for search state. This hook
 * parses it into typed state and writes updates back (replace, so filter tweaks
 * don't flood history). Pagination is owned by useInfiniteQuery, not the URL.
 */
export function useSearchUrlState() {
  const [params, setParams] = useSearchParams();

  const state: SearchUrlState = useMemo(() => {
    const num = (v: string | null) => (v != null && v !== '' && !Number.isNaN(Number(v)) ? Number(v) : undefined);
    const sortRaw = params.get('sort') as SearchSort | null;
    return {
      destination: params.get('destination') ?? '',
      checkIn: params.get('checkIn') ?? '',
      checkOut: params.get('checkOut') ?? '',
      guests: num(params.get('guests')) ?? 1,
      priceMin: num(params.get('priceMin')),
      priceMax: num(params.get('priceMax')),
      amenities: (params.get('amenities') ?? '').split(',').map((a) => a.trim()).filter(Boolean),
      sort: sortRaw && SORTS.includes(sortRaw) ? sortRaw : 'recommended',
    };
  }, [params]);

  const patch = useCallback(
    (next: Partial<SearchUrlState>) => {
      setParams(
        (prev) => {
          const merged = new URLSearchParams(prev);
          const apply = (key: string, value: string | undefined | null) => {
            if (value == null || value === '') merged.delete(key);
            else merged.set(key, value);
          };
          if ('destination' in next) apply('destination', next.destination);
          if ('checkIn' in next) apply('checkIn', next.checkIn);
          if ('checkOut' in next) apply('checkOut', next.checkOut);
          if ('guests' in next) apply('guests', next.guests != null ? String(next.guests) : undefined);
          if ('priceMin' in next) apply('priceMin', next.priceMin != null ? String(next.priceMin) : undefined);
          if ('priceMax' in next) apply('priceMax', next.priceMax != null ? String(next.priceMax) : undefined);
          if ('amenities' in next) apply('amenities', next.amenities && next.amenities.length ? next.amenities.join(',') : undefined);
          if ('sort' in next) apply('sort', next.sort && next.sort !== 'recommended' ? next.sort : undefined);
          return merged;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const clearFilters = useCallback(() => {
    patch({ priceMin: undefined, priceMax: undefined, amenities: [] });
  }, [patch]);

  return { state, patch, clearFilters };
}
